import {
  Box,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  Heading,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useRecoilValue } from "recoil";
import { FaTools } from "react-icons/fa";
import styled from "@emotion/styled";

import RemoteStart from "./operatiions/RemoteStart";
import RemoteStop from "./operatiions/RemoteStop";
import ChangeAvailability from "./operatiions/ChangeAvailability";
import TriggerMessage from "./operatiions/TriggerMessage";
import UnlockConnector from "./operatiions/UnlockConnector";
import ConnectorStatus from "./ConnectorStatus";
import { activeConnectorState } from "atoms/activeConnectorState";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import Card from "components/card/Card";

// Glass morphism styled components
const GlassMorphismBox = styled(Box)`
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  border: 1px solid rgba(35, 169, 121, 0.25);

  &:hover {
    border-color: rgba(35, 169, 121, 0.4);
  }
`;

const GlassMorphismCard = styled(Card)`
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  border: 1px solid rgba(35, 169, 121, 0.25);

  &:hover {
    border-color: rgba(35, 169, 121, 0.4);
  }
`;

const remoteOperations = [
  // { displayName: "Remote Start", value: "RemoteStart" },
  // { displayName: "Remote Stop", value: "RemoteStop" },
  { displayName: "Change Availability", value: "ChangeAvailability" },
  { displayName: "Trigger Message", value: "TriggerMessage" },
  { displayName: "Unlock Connector", value: "UnlockConnector" },
];
const RemoteOperations = ({ refresh }) => {
  const activeConnector = useRecoilValue(activeConnectorState);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit = accessLevels[role]?.accessLevel <= 2;
  const [selectedOperation, setSelectedOperation] = useState("");

  const status = activeConnector?.Status
    ? activeConnector.Status
    : "Unavailable";

  // Create radio options with tooltips for operations
  const options = remoteOperations.map((ele, index) => {
    let isDisabled;
    if (
      status === "Unavailable" &&
      ele.value !== "ChangeAvailability" &&
      ele.value !== "TriggerMessage"
    ) {
      isDisabled = true;
    } else if (
      ele.value === "RemoteStart" &&
      (status === "Charging" || status !== "Preparing")
    ) {
      isDisabled = true;
    } else if (ele.value === "RemoteStop" && status !== "Charging") {
      isDisabled = true;
    } else {
      isDisabled =
        !canEdit &&
        (ele.value === "ChangeAvailability" || ele.value === "TriggerMessage");
    }
    return (
      <Tooltip
        hasArrow
        label={isDisabled ? "Not available in current state" : ele.displayName}
        placement="right"
        bg="brand.500"
        color="white"
      >
        <Radio
          isDisabled={isDisabled || !canEdit}
          key={index}
          value={ele.value}
          colorScheme="brand"
          borderColor={"brand.500"}
          size="md"
        >
          {ele.displayName}
        </Radio>
      </Tooltip>
    );
  });

  const changeHandler = (e) => {
    setSelectedOperation(e);
  };

  return (
    <GlassMorphismBox
      w="100%"
      maxW="fit-content"
      minW={{ base: "100%", md: 500 }}
      mt={{ base: 4, md: 8 }}
      borderRadius={16}
      pt={2}
      px={{ base: 3, md: 5 }}
      pb={{ base: 3, md: 5 }}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "5px",
        // background: "linear-gradient(90deg, #23A979 0%, #ff9966 100%)",
        zIndex: 1,
      }}
    >
      <ConnectorStatus />
      <Flex
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        position={"relative"}
      >
        <Flex align="center" justify="center" mt={2} mb={2}>
          <Icon as={FaTools} mr={2} color="brand.500" boxSize={{ base: 4, md: 5 }} />
          <Heading size={{ base: "sm", md: "md" }} fontWeight={600} color="gray.700">
            Remote Operations
          </Heading>
        </Flex>
      </Flex>
      <Flex mt={4} borderRadius={0} gap={{ base: 3, md: 5 }} direction={{ base: "column", md: "row" }}>
        <RadioGroup
          defaultValue={status === "Unavailable" ? "" : selectedOperation}
          onChange={(e) => changeHandler(e)}
          value={selectedOperation}
          variant="checkbox"
          borderRadius={12}
          h={"auto"}
          overflow="hidden"
          isDisabled={!canEdit}
          w={{ base: "100%", md: "250px" }}
          bg={useColorModeValue(
            "rgba(255, 255, 255, 0.8)",
            "rgba(26, 32, 44, 0.8)"
          )}
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
          borderWidth="1px"
          borderColor="rgba(35, 169, 121, 0.3)"
        >
          <Box
            fontSize={{ base: 14, md: 16 }}
            fontWeight={700}
            bg="brand.500"
            borderRadius="0"
            textAlign="center"
            color="white"
            w="100%"
            py={{ base: 1.5, md: 2 }}
            mb={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FaTools} mr={2} boxSize={{ base: 4, md: 5 }} />
            Select Operation
          </Box>
          <Stack spacing={{ base: 3, md: 4 }} direction="column" p={{ base: 3, md: 4 }}>
            <Radio display={"hidden"} isDisabled={true} value="">
              Not Selected
            </Radio>
            {options.map((radio, index) => (
              <Box
                key={index}
                transition="all 0.2s"
                _hover={{
                  transform: !radio.props.isDisabled
                    ? "translateX(5px)"
                    : "none",
                }}
              >
                {React.cloneElement(radio, {
                  colorScheme: "brand",
                  borderColor: "brand.500",
                  sx: {
                    ".chakra-radio__control": {
                      borderColor: "brand.500",
                      _checked: {
                        bg: "brand.500",
                        borderColor: "brand.500",
                      },
                    },
                  },
                })}
              </Box>
            ))}
          </Stack>
        </RadioGroup>
        <GlassMorphismCard
          width="auto"
          borderRadius={12}
          alignItems="center"
          justifyContent={status === "Unavailable" ? "center" : "start"}
          flex={1}
          p={4}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "3px",
            // background: "linear-gradient(90deg, #23A979 0%, #ff9966 100%)",
            zIndex: 1,
          }}
        >
          {selectedOperation === "" && (
            <Flex
              justifySelf={"center"}
              direction="column"
              align="center"
              justify="center"
              py={8}
              px={4}
            >
              <Text
                fontWeight={700}
                fontSize={20}
                color={status === "Unavailable" ? "red.500" : "gray.400"}
                textAlign="center"
              >
                {status === "Unavailable"
                  ? "Charger Offline"
                  : "Please select an Operation"}
              </Text>
              {status !== "Unavailable" && (
                <Text fontSize="sm" color="gray.500" mt={2} textAlign="center">
                  Select an operation from the menu on the left to proceed
                </Text>
              )}
            </Flex>
          )}
          {selectedOperation === "RemoteStart" && (
            <RemoteStart refresh={refresh} />
          )}
          {selectedOperation === "RemoteStop" && (
            <RemoteStop refresh={refresh} />
          )}
          {selectedOperation === "ChangeAvailability" && (
            <ChangeAvailability refresh={refresh} />
          )}
          {selectedOperation === "TriggerMessage" && (
            <TriggerMessage refresh={refresh} />
          )}
          {selectedOperation === "UnlockConnector" && (
            <UnlockConnector refresh={refresh} />
          )}
        </GlassMorphismCard>
      </Flex>
    </GlassMorphismBox>
  );
};

export default RemoteOperations;
